/**
 * Custom hook that encapsulates the shared form-level state and side-effects
 * for the user create/edit dialog used by both UsersPage and UserManager.
 *
 * Extracted state:
 *   - formData + setFormData
 *   - validationErrors + setValidationErrors
 *   - org-data lists (subCompanies, branches, subCustomers) + loading flag
 *   - selected IDs (selectedSubCompanyId, selectedBranchId, selectedSubCustomerId)
 *
 * Extracted effects:
 *   - Resolves sub-company and branch IDs when org data loads for an edit
 *   - Resets branch when sub-company changes (user-driven only)
 *   - Resets org selections when accountType changes
 */
import { useState, useEffect, useRef } from 'react';
import { User, AccountType, AccessTypeAnalytics } from '../../types/auth';
import { SubCompany, Branch, SubCustomer } from '../../types/company';
import { companyApi } from '../../services/api/companyApi';
import { CreateUserData } from './userFormShared';

export interface UseUserFormStateReturn {
  formData: CreateUserData;
  setFormData: React.Dispatch<React.SetStateAction<CreateUserData>>;
  validationErrors: Record<string, string>;
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  subCompanies: SubCompany[];
  branches: Branch[];
  subCustomers: SubCustomer[];
  selectedSubCompanyId: number | null;
  setSelectedSubCompanyId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedBranchId: number | null;
  setSelectedBranchId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedSubCustomerId: number | null;
  setSelectedSubCustomerId: React.Dispatch<React.SetStateAction<number | null>>;
  loadingOrgData: boolean;
  loadOrgData: (companyId: number) => Promise<void>;
}

export const useUserFormState = (
  editingUser: User | null,
  initialCompanyId: number,
): UseUserFormStateReturn => {
  const [formData, setFormData] = useState<CreateUserData>({
    firstName: '',
    lastName: '',
    email: '',
    accountType: AccountType.SubCustomerAdmin,
    companyId: initialCompanyId,
    branch: '',
    subCompany: '',
    mail: '',
    accessTypeAnalytics: AccessTypeAnalytics.Passive,
    dataStorageKey: '',
    passKey: '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [subCompanies, setSubCompanies] = useState<SubCompany[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [subCustomers, setSubCustomers] = useState<SubCustomer[]>([]);
  const [selectedSubCompanyId, setSelectedSubCompanyId] = useState<number | null>(null);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [selectedSubCustomerId, setSelectedSubCustomerId] = useState<number | null>(null);
  const [loadingOrgData, setLoadingOrgData] = useState(false);

  const loadOrgData = async (companyId: number) => {
    try {
      setLoadingOrgData(true);
      const [subCompaniesData, branchesData, subCustomersData] = await Promise.all([
        companyApi.getSubCompanies(companyId),
        companyApi.getBranches(companyId),
        companyApi.getSubCustomers(companyId),
      ]);
      setSubCompanies(subCompaniesData);
      setBranches(branchesData);
      setSubCustomers(subCustomersData);
    } catch (error) {
      console.error('Failed to load organizational data:', error);
    } finally {
      setLoadingOrgData(false);
    }
  };

  // Resolve sub-company and branch IDs after org data loads for editing
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!editingUser || subCompanies.length === 0) return;

    if (editingUser.subCompany) {
      const sc = subCompanies.find(
        (s) => s.code === editingUser.subCompany || s.name === editingUser.subCompany,
      );
      if (sc) {
        setSelectedSubCompanyId(sc.id);
      }
    }

    if (
      editingUser.branch &&
      editingUser.accountType === AccountType.BranchAdmin &&
      branches.length > 0
    ) {
      const br = branches.find(
        (b) => b.code === editingUser.branch || b.name === editingUser.branch,
      );
      if (br) {
        setSelectedBranchId(br.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCompanies, branches, editingUser]);

  // Reset branch selection when sub-company changes (only for user-driven changes, not edit init)
  const prevSubCompanyIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      prevSubCompanyIdRef.current !== null &&
      selectedSubCompanyId !== null &&
      prevSubCompanyIdRef.current !== selectedSubCompanyId
    ) {
      setSelectedBranchId(null);
      setFormData((prev) => ({ ...prev, branch: '' }));
    }
    prevSubCompanyIdRef.current = selectedSubCompanyId;
  }, [selectedSubCompanyId]);

  // Reset organizational selections when account type changes
  useEffect(() => {
    if (
      formData.accountType !== AccountType.SubCompanyAdmin &&
      formData.accountType !== AccountType.BranchAdmin
    ) {
      setSelectedSubCompanyId(null);
      setSelectedBranchId(null);
    }
    if (formData.accountType !== AccountType.SubCustomerAdmin) {
      setSelectedSubCustomerId(null);
    }
  }, [formData.accountType]);

  return {
    formData,
    setFormData,
    validationErrors,
    setValidationErrors,
    subCompanies,
    branches,
    subCustomers,
    selectedSubCompanyId,
    setSelectedSubCompanyId,
    selectedBranchId,
    setSelectedBranchId,
    selectedSubCustomerId,
    setSelectedSubCustomerId,
    loadingOrgData,
    loadOrgData,
  };
};
